import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiError, LegacyKuduResponseEnvelope } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';
import type {
  ComponentPageData,
  RawComponent,
  RawQuestion,
  RawQuestionSection,
  RawQuestionSettings,
  RawSection,
} from '@/features/content/useComponent';

import {
  buildUpdateComponentSettingsPayload,
  type UpdateCoursePayload,
} from './useUpdateComponentSettings';

export interface AssignPreLectureInput {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  selectedQuestionIds: string[];
}

interface RawUpdateCourseResponse {
  components?: RawComponent[] | null;
}

function createDataError(message: string): ApiError {
  const error = new Error(message) as ApiError;
  error.kind = 'data';
  return error;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function toQuestionArray(value: unknown): RawQuestion[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawQuestion[];
}

function getSeededPreLectureQuestionSettings(
  component: RawComponent,
): RawQuestionSettings {
  const homeworkSettings =
    component.question_settings ??
    component.questionSettings ??
    component.course_question_settings ??
    component.courseQuestionSettings ??
    {};

  return {
    ...(homeworkSettings as RawQuestionSettings),
    type: 1,
    user_defined_numbers: [],
    userDefinedNumbers: [],
  };
}

function getSelectionMap(component: ComponentPageData, selectedQuestionIds: string[]) {
  const selectedIds = new Set(selectedQuestionIds.map((id) => normalizeString(id)).filter(Boolean));
  const selectedOptions = component.controlPanel.homeworkQuestionOptions.filter((option) =>
    selectedIds.has(option.id),
  );

  if (selectedOptions.length === 0) {
    throw createDataError('Select at least one homework question to assign.');
  }

  return selectedOptions.reduce<Map<number, Set<number>>>((map, option) => {
    if (
      option.sectionIndex === undefined ||
      option.questionIndexWithinSection === undefined
    ) {
      throw createDataError(
        'Homework question ownership metadata is missing from the component payload.',
      );
    }

    const current = map.get(option.sectionIndex) ?? new Set<number>();
    current.add(option.questionIndexWithinSection);
    map.set(option.sectionIndex, current);
    return map;
  }, new Map<number, Set<number>>());
}

export function buildAssignPreLectureUpdatePayload(
  component: ComponentPageData,
  courseId: string,
  selectedQuestionIds: string[],
): UpdateCoursePayload {
  if (component.controlPanel.preLectureQuestionOptions.length > 0) {
    throw createDataError(
      'Pre-lecture questions already exist for this component.',
    );
  }

  const selectionBySection = getSelectionMap(component, selectedQuestionIds);

  return buildUpdateComponentSettingsPayload(component, courseId, (draft) => {
    const sections = Array.isArray(draft.sections) ? (draft.sections as RawSection[]) : [];
    let movedQuestionCount = 0;

    sections.forEach((section, sectionIndex) => {
      const selectedQuestionIndexes = selectionBySection.get(sectionIndex);
      const homeworkQuestionSection = isRecord(
        section.question_section ?? section.questionSection,
      )
        ? ((section.question_section ?? section.questionSection) as RawQuestionSection)
        : null;

      if (!homeworkQuestionSection || !selectedQuestionIndexes?.size) {
        return;
      }

      const homeworkQuestions = toQuestionArray(homeworkQuestionSection.questions);
      const movedQuestions: RawQuestion[] = [];
      const remainingHomeworkQuestions: RawQuestion[] = [];

      homeworkQuestions.forEach((question, questionIndexWithinSection) => {
        if (selectedQuestionIndexes.has(questionIndexWithinSection)) {
          movedQuestions.push(question);
        } else {
          remainingHomeworkQuestions.push(question);
        }
      });

      if (movedQuestions.length === 0) {
        return;
      }

      movedQuestionCount += movedQuestions.length;

      const existingPreLectureQuestionSection = isRecord(
        section.pre_lecture_question_section ?? section.preLectureQuestionSection,
      )
        ? ((
            section.pre_lecture_question_section ?? section.preLectureQuestionSection
          ) as RawQuestionSection)
        : null;
      const existingPreLectureQuestions = toQuestionArray(
        existingPreLectureQuestionSection?.questions,
      );

      section.question_section = {
        ...homeworkQuestionSection,
        questions: remainingHomeworkQuestions,
      };
      delete (section as RawSection & { questionSection?: unknown }).questionSection;

      section.pre_lecture_question_section = {
        ...(existingPreLectureQuestionSection ?? {}),
        questions: [...existingPreLectureQuestions, ...movedQuestions],
      };
      delete (section as RawSection & { preLectureQuestionSection?: unknown })
        .preLectureQuestionSection;
    });

    if (movedQuestionCount === 0) {
      throw createDataError('Unable to match the selected homework questions.');
    }

    const nextSettings = getSeededPreLectureQuestionSettings(draft);
    draft.pre_lecture_question_settings = nextSettings;
    draft.preLectureQuestionSettings = nextSettings;
  });
}

function unwrapUpdatedComponent(
  response:
    | RawUpdateCourseResponse
    | RawComponent
    | LegacyKuduResponseEnvelope<RawUpdateCourseResponse>
    | null
    | undefined,
  fallbackComponent: RawComponent,
) {
  if (response && typeof response === 'object' && 'Response' in response) {
    return unwrapUpdatedComponent(
      response.Response as RawUpdateCourseResponse | RawComponent,
      fallbackComponent,
    );
  }

  if (
    response &&
    typeof response === 'object' &&
    'components' in response &&
    Array.isArray(response.components)
  ) {
    return (response.components[0] as RawComponent | undefined) ?? fallbackComponent;
  }

  return (response as RawComponent | null | undefined) ?? fallbackComponent;
}

export function useAssignPreLecture() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getIdToken, user } = useAuth();

  return useMutation<RawComponent, ApiError, AssignPreLectureInput>({
    mutationFn: async ({ component, courseId, selectedQuestionIds }) => {
      if (!user) {
        throw createDataError('You must be signed in to assign pre-lecture questions.');
      }

      const payload = buildAssignPreLectureUpdatePayload(
        component,
        courseId,
        selectedQuestionIds,
      );
      const [draftComponent] = payload.components;
      const returnTo = `${location.pathname}${location.search}${location.hash}`;
      const client = createApiClient({
        baseUrl: import.meta.env.VITE_API_BASE_URL || '',
        getIdToken,
        getLegacyToken: async () => createLegacyKuduToken(user),
        onAuthError: () =>
          navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
            replace: true,
          }),
      });

      const response = await client.invokeKuduMethod<
        RawUpdateCourseResponse | LegacyKuduResponseEnvelope<RawUpdateCourseResponse>,
        UpdateCoursePayload
      >('UpdateCourse', payload);

      return unwrapUpdatedComponent(response, draftComponent);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['component', variables.courseId, variables.componentId],
      });
    },
  });
}
